import React, { memo, useCallback } from 'react';

import { areEqual } from 'Utils/equalityChecks';

import { SupportConversation } from 'Context/Support';

import classes from './conversationList.module.css';

function formatTime(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
}

function statusClass(status: string): string {
  const s = status?.toLowerCase();
  if (s === 'open' || s === 'active') return classes.statusOpen;
  if (s === 'pending' || s === 'awaiting_reply') return classes.statusPending;
  return classes.statusClosed;
}

interface Props {
  conversation: SupportConversation;
  onClick: (id: number) => void;
}

function ConversationListItem({ conversation, onClick }: Props) {
  const { id, subject, status, unread_count, last_message_at, latest_message } = conversation;

  const handleClick = useCallback(() => onClick(id), [id, onClick]);

  const preview = latest_message?.body || '';

  return (
    <div className={classes.item} onClick={handleClick} role="button" tabIndex={0} onKeyDown={handleClick}>
      <div className={classes.itemContent}>
        <div className={classes.itemHeader}>
          <span className={classes.itemSubject}>{subject}</span>
          <span className={`${classes.statusBadge} ${statusClass(status)}`}>{status}</span>
        </div>
        {preview && <div className={classes.itemPreview}>{preview}</div>}
      </div>
      <div className={classes.itemMeta}>
        <span className={classes.itemTime}>{formatTime(last_message_at)}</span>
        {unread_count > 0 && <span className={classes.unreadBadge}>{unread_count}</span>}
      </div>
    </div>
  );
}

const ConversationListItemMemo = memo(ConversationListItem, areEqual);

export { ConversationListItemMemo as ConversationListItem };
